import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { supabase } from "../utils/supabaseClient";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUserPremium, setIsUserPremium] = useState(false);
  const navigate = useNavigate();

  const checkPremium = useCallback(async (userId) => {
    if (!userId) {
      setIsUserPremium(false);
      return;
    }
    const isFreeMode = process.env.REACT_APP_FREE_ACCESS_MODE === "true";
    if (isFreeMode) {
      setIsUserPremium(true);
      return;
    }
    try {
      const { data } = await supabase
        .from("subscriptions")
        .select("status")
        .eq("user_id", userId)
        .in("status", ["active", "trialing"])
        .maybeSingle();
      setIsUserPremium(!!data);
    } catch {
      setIsUserPremium(false);
    }
  }, []);

  useEffect(() => {
    // Check for an active session on mount
    const getSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          throw error;
        }

        setSession(data.session);
        const sessionUser = data.session?.user || null;
        setUser(sessionUser);
        await checkPremium(sessionUser?.id);
      } catch (error) {
        // Error getting session
      } finally {
        setIsLoading(false);
      }
    };

    getSession();

    // Subscribe to auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      const sessionUser = session?.user || null;
      setUser(sessionUser);
      await checkPremium(sessionUser?.id);
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [checkPremium]);

  const signIn = async (email, password) => {
    try {
      setIsLoading(true);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw error;
      }

      setUser(data.user);
      setSession(data.session);
      toast.success("Successfully signed in!");
      navigate("/home");
    } catch (error) {
      toast.error(`Authentication error: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setIsLoading(true);

      // Environment-aware redirect URL
      const isLocalhost =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      const redirectUrl = isLocalhost
        ? "http://localhost:3000/home"
        : "https://bramlo.com/home";

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: redirectUrl,
        },
      });

      if (error) {
        throw error;
      }

      // No need to set user or session here as the auth state change listener will handle it
      toast.success("Redirecting to Google...");
    } catch (error) {
      toast.error(`Google sign-in error: ${error.message}`);
      setIsLoading(false);
      throw error;
    }
  };

  const signUp = async (email, password, metadata) => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
        },
      });

      if (error) {
        throw error;
      }

      // AUTO-GRANT FREE SUBSCRIPTION if in free access mode
      const isFreeAccessMode =
        process.env.REACT_APP_FREE_ACCESS_MODE === "true";
      if (isFreeAccessMode && data.user && data.user.id) {
        try {
          const { error: subError } = await supabase
            .from("subscriptions")
            .insert({
              user_id: data.user.id,
              status: "active",
            });

          if (subError) {
            // Don't block signup if subscription creation fails
          }
        } catch (subError) {
          // Don't block signup if subscription creation fails
        }
      }

      if (!data.session) {
        toast.info("Please check your email for the confirmation link.");
      } else {
        setUser(data.user);
        setSession(data.session);
        toast.success("Account created successfully!");
        navigate("/home");
      }
    } catch (error) {
      toast.error(`Registration error: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    setUser(null);
    setSession(null);
    setIsLoading(false);
    Object.keys(localStorage)
      .filter((k) => k.startsWith("sb-") && k.includes("auth-token"))
      .forEach((k) => localStorage.removeItem(k));
    toast.success("Successfully signed out!");
    navigate("/");
    supabase.auth.signOut({ scope: "local" }).catch((error) => {
      console.error("Sign out error:", error);
    });
  };

  const resetPassword = async (email) => {
    try {
      setIsLoading(true);

      // Environment-aware redirect URL
      const isLocalhost =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      const redirectUrl = isLocalhost
        ? "http://localhost:3000/reset-password"
        : "https://bramlo.com/reset-password";

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        throw error;
      }

      toast.success("Please check your email for password reset instructions.");
    } catch (error) {
      toast.error(`Password reset error: ${error.message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    session,
    isLoading,
    signIn,
    signInWithGoogle,
    signUp,
    signOut,
    resetPassword,
    isAuthenticated: !!user,
    isUserPremium,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
